import React, { useRef, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// 导入公共资产
import brush from '../../sources/Graph/Brush_purple.png';
import aboutUs_1 from '../../sources/Graph/AboutUs_1.png';
import aboutUs_2 from '../../sources/Graph/AboutUs_2.png';
import aboutUs_3 from '../../sources/Graph/AboutUs_3.png';
import aboutUs_4 from '../../sources/Graph/AboutUs_4.png';

// 滚动动效组件 (Scroll Reveal Helper)
const RevealOnScroll = ({ children, direction = "up", delay = 0 }) => {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                // 不再断开监听，而是根据其是否在视口中反复设置可见状态
                setIsVisible(entry.isIntersecting);
            },
            { 
                threshold: 0.2, // 出现 20% 时触发淡入，离开低于 20% 时触发淡出
                rootMargin: "0px 0px -10% 0px" // 当组件底部离开视口太远时也会提前淡出
            } 
        );
        const currentRef = ref.current;
        if (currentRef) {
            observer.observe(currentRef);
        }
        return () => {
            if (currentRef) observer.unobserve(currentRef);
        };
    }, []);

    let transformStart = "translateY(50px)";
    if (direction === "left") transformStart = "translateX(-50px)";
    if (direction === "right") transformStart = "translateX(50px)";

    return (
        <Box
            ref={ref}
            sx={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translate(0, 0)" : transformStart,
                transition: `all 1s cubic-bezier(0.17, 0.55, 0.55, 1) ${isVisible ? delay : 0}s`, // 淡出时不需要delay，立刻收回
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {children}
        </Box>
    );
};

export default function AboutUs() {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const textStyle = {
        fontFamily: "Noto Sans TC",
        color: "#4E433C",
        fontSize: { xs: 16, md: 24 },
        fontWeight: 400,
        lineHeight: "160%",
        textAlign: { xs: "center", md: "left" }
    };

    return (
        <Box width={"100%"} sx={{ py: 4, overflow: 'hidden' }}>
            {/* 顶部的标题 */}
            <Typography
                fontFamily={"Noto Sans TC"}
                textAlign={"center"}
                fontSize={{ xs: "28px", md: "42px" }}
                fontWeight={700}
                fontStyle={"normal"}
                color='#7DC4A2'
                py={3}
                mb={8}
                sx={{
                    textShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
                    lineHeight: '100%',
                    position: 'relative',
                    backgroundImage: `url(${brush})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundPositionY: { xs: 35, md: 55 }
                }}
            >
                {t('AboutUs.title')}
            </Typography>

            <Box sx={{ width: "100%", maxWidth: "1200px", margin: "0 auto", px: { xs: 3, md: 4 } }}>
                {/* 卡片 1: 左文 右图 */}
                <Grid container spacing={{ xs: 4, md: 8 }} sx={{ mt: 2, mb: { xs: 8, md: 16 }, alignItems: "center" }}>
                <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
                    <RevealOnScroll direction={isMobile ? "up" : "left"}>
                        <Typography sx={textStyle}>
                            {t('AboutUs.card1_text')}
                        </Typography>
                    </RevealOnScroll>
                </Grid>
                <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
                    <RevealOnScroll direction={isMobile ? "up" : "right"} delay={0.2}>
                        <Box sx={{ width: "100%", borderRadius: 4, overflow: 'hidden', boxShadow: "0px 10px 40px rgba(0,0,0,0.05)" }}>
                            <img src={aboutUs_1} alt="Step 1" style={{ width: "100%", height: "auto", display: 'block' }} />
                        </Box>
                    </RevealOnScroll>
                </Grid>
            </Grid>

            {/* 卡片 2: 左图 右文 (反转) */}
            <Grid container spacing={{ xs: 4, md: 8 }} sx={{ mb: { xs: 8, md: 16 }, alignItems: "center" }}>
                <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 1 } }}>
                    <RevealOnScroll direction={isMobile ? "up" : "left"}>
                        <Box sx={{ width: "100%", borderRadius: 4, overflow: 'hidden', boxShadow: "0px 10px 40px rgba(0,0,0,0.05)" }}>
                            <img src={aboutUs_2} alt="Step 2" style={{ width: "100%", height: "auto", display: 'block' }} />
                        </Box>
                    </RevealOnScroll>
                </Grid>
                <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 2 } }}>
                    <RevealOnScroll direction={isMobile ? "up" : "right"} delay={0.2}>
                        <Typography sx={textStyle}>
                            {t('AboutUs.card2_text1')}<br/><br/>
                            <strong>{t('AboutUs.card2_text2')}</strong>
                        </Typography>
                    </RevealOnScroll>
                </Grid>
            </Grid>

            {/* 卡片 3: 左文 右图 */}
            <Grid container spacing={{ xs: 4, md: 8 }} sx={{ mb: { xs: 8, md: 16 }, alignItems: "center" }}>
                <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
                    <RevealOnScroll direction={isMobile ? "up" : "left"}>
                        <Typography sx={textStyle}>
                            {t('AboutUs.card3_text')}
                        </Typography>
                    </RevealOnScroll>
                </Grid>
                <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
                    <RevealOnScroll direction={isMobile ? "up" : "right"} delay={0.2}>
                        <Box sx={{ width: "100%", borderRadius: 4, overflow: 'hidden', boxShadow: "0px 10px 40px rgba(0,0,0,0.05)" }}>
                            <img src={aboutUs_3} alt="Step 3" style={{ width: "100%", height: "auto", display: 'block' }} />
                        </Box>
                    </RevealOnScroll>
                </Grid>
            </Grid>

            {/* 卡片 4: 右图 左文 (反转, 恢复Z字型) */}
            <Grid container spacing={{ xs: 4, md: 8 }} sx={{ mb: { xs: 8, md: 16 }, alignItems: "center" }}>
                <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 1 } }}>
                    <RevealOnScroll direction={isMobile ? "up" : "left"}>
                        <Box sx={{ width: "100%", borderRadius: 4, overflow: 'hidden', boxShadow: "0px 10px 40px rgba(0,0,0,0.05)" }}>
                            <img src={aboutUs_4} alt="Vision" style={{ width: "100%", height: "auto", display: 'block' }} />
                        </Box>
                    </RevealOnScroll>
                </Grid>
                <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 2 } }}>
                    <RevealOnScroll direction={isMobile ? "up" : "right"} delay={0.2}>
                        <Typography sx={textStyle}>
                            {t('AboutUs.card4_text1')}<br/><br/>
                            <strong>{t('AboutUs.card4_text2')}</strong>
                        </Typography>
                    </RevealOnScroll>
                </Grid>
            </Grid>
            </Box>
        </Box>
    );
}
